import { Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { JsonViewer } from "@textea/json-viewer";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MaterialReactTable,
  useMaterialReactTable, MRT_ColumnFiltersState, MRT_SortingState, MRT_ColumnFilterFnsState,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCommandJournalQuery } from "@/api/hooks/command-journal-hooks";
import { useMuiTableToolbarAlertBannerProps } from "@/hooks/use-mui-table-toolbar-alert-banner-props";
import { useTableSettings } from "@/hooks/use-table-settings";
import { CommandJournalRegularDto, KeyFilter, KeyFilterModeEnum, KeySort } from "@/shared/api";
import { assembleKeyFilters, assembleSort } from "@/lib/cfg-utils";
import { assembleCommandJournalFiltersFns, assembleRegularAccountsFiltersFns } from "@/lib/accountsUtils";
import { COMMON_REFETCH_INTERVAL } from "@/lib/constants";

const CommandJournalTable = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation();
  const columns = useMemo<MRT_ColumnDef<CommandJournalRegularDto>[]>(
    () => [
      {
        id: "response_received_at",
        header: t("responseTime"),
        accessorFn: (row) =>
          row.response_received_at ? new Date(row.response_received_at).toLocaleString() : "-",
      },
      {
        id: "username",
        header: t("username"),
        accessorFn: (row) => row.username,
        //filterFn: "contains",
      },
      {
        id: "user_ip",
        header: t("userIp"),
        accessorFn: (row) => row.user_ip,
        filterFn: "contains",
      },
      {
        id: "domain",
        header: t("domain"),
        accessorFn: (row) => row.domain,
        filterFn: "contains",
      },
      {
        id: "subject_id", //action_subject_id
        header: t("subjectIdentifier"),
        accessorFn: (row) => row.action_subject_id,
        filterFn: "contains",
      },
      {
        id: "broker_topic",
        header: t("brokerTopic"),
        accessorFn: (row) => row.broker_topic,
      },
      {
        id: "type", //command type
        header: t("commandType"),
        accessorFn: (row) => mapCommandType(row.type),
        filterFn: "contains",
      },
      {
        id: "status",
        header: t("commandStatus"),
        accessorFn: (row) => mapCommandStatus(row),
        filterFn: "contains",
      },
      {
        id: "body", //body
        header: t("commandBody"),
        accessorFn: (row) => JSON.stringify(row.body, null, 2),
        filterFn: "contains",
        Cell: ({ row: { original } }) => (
          <JsonViewer
            style={{ maxWidth: "120px", wordBreak: "break-word", whiteSpace: "pre-wrap" }}
            rootName="body"
            displayDataTypes={false}
            value={original.body && JSON.parse(original.body)?.body}
            defaultInspectDepth={0}
          />
        ),
      },
      {
        id: "created_at",
        header: t("executionTime"),
        accessorFn: (row) => new Date(row.created_at).toLocaleString(),
        filterFn: "contains",
      },
      {
        id: "response_body",
        header: t("responseBody"),
        accessorFn: (row) => JSON.stringify(row.response_body, null, 2),
        filterFn: "contains",
        Cell: ({ row: { original } }) => (
          <JsonViewer
            rootName="body"
            displayDataTypes={false}
            value={original.response_body && JSON.parse(original.response_body)}
            defaultInspectDepth={0}
          />
        ),
      },
      {
        id: "error_message",
        header: t("responseMessage"),
        accessorFn: (row) => row.error_message ?? "-",
        filterFn: "contains",
      },
    ],
    [t]
  );


  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
    assembleCommandJournalFiltersFns({ columns, searchParams: {} })
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "created_at", desc: true }]);

  const filters = useMemo<Set<KeyFilter>>(
    () => {
      return assembleKeyFilters(columnFilters, columnFilterFns, []);
    },
    [columnFilters, columnFilterFns/*, requiredFilters*/]
  );

  useEffect(() => {
    console.log(filters)
  }, [filters]);
  const sort = useMemo<KeySort[]>(() => assembleSort(sorting), [sorting]);


  const {
    data: commandJournalResponse,
    isError: isCommandJournalError,
    isLoading: isCommandJournalLoading,
    isRefetching: isCommandJournalRefetching,
    error: commandJournalError,
  } = useCommandJournalQuery({
    pagination,
    filters: filters,
    sort: sort,
  }, {
    enabled: true,
    refetchInterval: COMMON_REFETCH_INTERVAL,
  });

  const { defaultProps } = useTableSettings<CommandJournalRegularDto>("commandJournal");


  const muiToolbarAlertBannerProps = useMuiTableToolbarAlertBannerProps(
    Boolean(isCommandJournalError),
    commandJournalError
  );

  const table = useMaterialReactTable({
    data: (commandJournalResponse?.data.content ??
      []) as unknown as CommandJournalRegularDto[],
    columns: columns,
    ...defaultProps,
    //selectAllMode: "all",
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
      density: "compact",
      isFullScreen: true,
    },
    muiToolbarAlertBannerProps,
    state: {
      pagination,
      columnFilters: columnFilters,
      columnFilterFns: columnFilterFns,
      sorting: sorting,
      isLoading: isCommandJournalLoading,
      showProgressBars: isCommandJournalRefetching,
    },
    rowCount: commandJournalResponse?.data.totalElements,
    pageCount: commandJournalResponse?.data.totalPages,
    enableSelectAll: false,
    enableRowSelection: false,
    enableGrouping: false,
    enableColumnDragging: false,
    enableColumnFilterModes: false,

    renderToolbarInternalActions: () => (
      <>
        <IconButton onClick={() => onClose && onClose()}>
          <Close />
        </IconButton>
      </>
    ),
    enableHiding: false,
    enableColumnFilters: true,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,


    enablePagination: true,
    enableStickyHeader: true,
    enableSorting: true,
    autoResetPageIndex: false,
    manualPagination: true,
    manualFiltering: true,
    /*sorting: sorting,*/
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    /*setSorting: setSorting,*/
  });

  return <MaterialReactTable table={table} />;
};

export default CommandJournalTable;

function mapCommandStatus(journalRecord: CommandJournalRegularDto): string {
  if (journalRecord.status === -1) return "FAILED (-1)";
  if (journalRecord.status === 1) return "SUCCESS (1)";

  if (journalRecord.status === 0) {

    if([51, 52, 53, 100, 101, 102, 103].includes(journalRecord.type)) return "NOT A COMMAND. ACTION COMPLETED"

    const expired = journalRecord.created_at + journalRecord.response_wait_time;

    if (Date.now() > expired) {
      return "RESPONSE_NOT_RECEIVED_IN_TIME";
    }

    return "COMMAND_SENT";
  }

  return "UNKNOWN STATUS";
}

function mapCommandType(commandType: number | undefined): string {
  if (commandType === 1) return "UPDATE NICKNAME (1)";
  if (commandType === 2) return "UPDATE AVATAR (2)";
  if (commandType === 3) return "RUN ACCOUNT (3)";
  if (commandType === 4) return "STOP ACCOUNT (4)";
  if (commandType === 5) return "MIGRATE ACCOUNT (5)";
  if (commandType === 6) return "JOIN CLUB (6)";
  if (commandType === 7) return "LEAVE CLUB (7)";
  if (commandType === 8) return "REQUEST REPLAY (8)";
  if (commandType === 9) return "SWITCH GS MODE (9)";
  if (commandType === 10) return "SWITCH TS MODE (10)";
  if (commandType === 11) return "REGISTER (11)";

  if (commandType === 51) return "ENABLE (51)";
  if (commandType === 52) return "DISABLE (52)";
  if (commandType === 53) return "UPSERT_CONFIG (53)";
  if (commandType === 54) return "DELETE_CONFIG (54)";

  if (commandType === 100) return "CREATE_USER (100)";
  if (commandType === 101) return "DELETE_USER (101)";
  if (commandType === 102) return "UPDATE_USER (102)";
  if (commandType === 103) return "CHANGE_USER_PASSWORD (103)";

  return "UNKNOWN COMMAND TYPE";
}
